<template>
  <div class="role-service-provider-form">
    <!-- Role RoleServiceProviderMachine -->
    <div ref="machineList">
      <RoleServiceProviderMachineComponent
        v-for="element in roleServiceProviderMachines"
        :key="element.id"
        :ref="(component) => setMachineComponent(element.id, component)"
        :model-value="element"
        :selectable-machines="selectableMachines"
        @update:model-value="roleServiceProviderMachineController.update"
        @delete="roleServiceProviderMachineController.delete"
      />
    </div>

    <!-- New RoleServiceProviderMachine -->
    <RoleServiceProviderMachineComponent
      v-if="newRoleServiceProviderMachine !== null"
      :model-value="newRoleServiceProviderMachine"
      :selectable-machines="selectableMachines"
      @update:model-value="createRoleServiceProviderMachine"
      @delete="onNewServiceProviderMachineButtonClicked"
      ref="newRoleServiceProviderMachineComponentRef"
    />
    <PButton
      size="sm"
      @click="onNewServiceProviderMachineButtonClicked"
      :icon="['fas', 'plus']"
      class="mb-2"
    >
      Machine toevoegen
    </PButton>
    <div>
      <PFormField label="Locatie" sr-only>
        <PInputCombobox
          v-model="modelValue.location"
          :items="tenantBaseLocations"
          :disable-control="$attrs.disabled as boolean"
          items-key-name="id"
          items-value-name="name"
          :creation-model-type="Location"
          input-placeholder="Locatie"
          @item-selected="updateRole"
          @item-deselected="updateRole"
          return-null-if-no-matches
        />
      </PFormField>
    </div>
  </div>
</template>

<script setup lang="ts">
import PFormField from "@/components/form/PFormField.vue"
import { computed, inject, nextTick, ref, watch } from "vue"
import PInputCombobox from "@/components/form/PInputCombox.vue"
import { storeToRefs } from "pinia"
import { useLocationStore } from "@/store/locations.store"
import { RoleServiceProvider } from "@/models/role/role-service-provider.model"
import { Location } from "@/models/location.model"
import PButton from "@/components/elements/PButton.vue"
import { useSortableList, type SortableListChange } from "@/composables/use-sortable-list"
import type { ComponentPublicInstance } from "vue"
import { RoleServiceProviderMachine } from "@/models/role-service-provider-machine.model.ts"
import { ModelFactory } from "@/factories/model-factory.ts"
import RoleServiceProviderMachineComponent from "@/components/role/RoleServiceProviderMachineComponent.vue"
import type { RoleServiceProviderMachineController } from "@/controllers/model-controllers/role-service-provider-machine-controller.ts"
import type { IRoleServiceProviderMachine } from "@/interfaces/api/role-service-provider-machine.interface.ts"
import { useMachineStore } from "@/store/machines.store.ts"
import { useRoleStore } from "@/store/roles.store.ts"

const modelValue = defineModel<RoleServiceProvider>({ required: true })
const emit = defineEmits(["update:modelValue", "roleChanged"])

const roleServiceProviderMachines = ref<RoleServiceProviderMachine[]>([])
// Keep the role service provider machines sorted by sortIndex
watch(
  () => modelValue.value.roleServiceProviderMachines.map((rspm) => rspm.sortIndex),
  async () => {
    await nextTick()
    roleServiceProviderMachines.value = [...modelValue.value.roleServiceProviderMachines].sort(
      (a, b) => a.sortIndex - b.sortIndex
    )
  },
  { immediate: true }
)

const locationStore = useLocationStore()
const { tenantBaseLocations } = storeToRefs(locationStore)

const machineStore = useMachineStore()
const { machines } = storeToRefs(machineStore)
const selectableMachines = computed(() => {
  return machines.value.filter((machine) => {
    return !roleServiceProviderMachines.value.some((rspm) => rspm.machine?.id === machine.id)
  })
})

const roleServiceProviderMachineController: RoleServiceProviderMachineController = inject(
  "roleServiceProviderMachineController"
)!
const newRoleServiceProviderMachine = ref<RoleServiceProviderMachine | null>(null)
/**
 * Persists the changed sort order after Vue has reordered the machine rows.
 */
const machineList = ref<HTMLElement | null>(null)
useSortableList(machineList, roleServiceProviderMachines, {
  handle: ".drag-handle",
  onChange: onOrderChange
})

function onOrderChange(change: SortableListChange<RoleServiceProviderMachine>) {
  if (!change.moved) return
  const { oldIndex, newIndex } = change.moved

  if (oldIndex === newIndex) {
    return // no change in order
  }

  // figure out the slice that shifted
  const start = Math.min(oldIndex, newIndex)
  const end = Math.max(oldIndex, newIndex)

  // only re-assign & persist those
  for (let i = start; i <= end; i++) {
    roleServiceProviderMachines.value[i].sortIndex = i
    roleServiceProviderMachineController.update(roleServiceProviderMachines.value[i])
  }
}

const roleStore = useRoleStore()
function updateRole() {
  roleStore.updateRole(modelValue.value)
  emit("roleChanged")
}

const machineComponents = new Map<
  string,
  InstanceType<typeof RoleServiceProviderMachineComponent>
>()
function setMachineComponent(
  id: string | undefined,
  component: Element | ComponentPublicInstance | null
) {
  if (!id) return
  if (component)
    machineComponents.set(id, component as InstanceType<typeof RoleServiceProviderMachineComponent>)
  else machineComponents.delete(id)
}
async function createRoleServiceProviderMachine(
  roleServiceProviderMachine: RoleServiceProviderMachine
) {
  await roleServiceProviderMachineController.create(roleServiceProviderMachine)
  newRoleServiceProviderMachine.value = null

  // Focus the purchase item combobox just after creation
  await nextTick()
  if (roleServiceProviderMachine.id)
    machineComponents.get(roleServiceProviderMachine.id)?.focusPurchaseItemCombobox()
}

const newRoleServiceProviderMachineComponentRef = ref<typeof RoleServiceProviderMachineComponent>()
async function onNewServiceProviderMachineButtonClicked() {
  newRoleServiceProviderMachine.value = ModelFactory.createModel(RoleServiceProviderMachine, {
    roleServiceProvider: modelValue.value.id,
    sortIndex: getNextSortIndex()
  } as IRoleServiceProviderMachine)
  await nextTick()
  newRoleServiceProviderMachineComponentRef.value!.focusMachineCombobox()
}

function getNextSortIndex(): number {
  if (roleServiceProviderMachines.value.length === 0) {
    return 0
  }
  return Math.max(...roleServiceProviderMachines.value.map((rspm) => rspm.sortIndex)) + 1
}
</script>

<style scoped></style>
